import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { DriverOrderStatus, distanceFeet, toLatLng } from './models';

// iOS ETAManager mirror — keeps the tracked order's estimatedPickupTime /
// estimatedDeliveryTime fresh as the driver moves.
//
// iOS uses MapKit directions (road network). Web uses the public OSRM demo
// server (driving profile, CORS-enabled) with a straight-line ~20mph fallback
// when routing fails. Writes only when the ETA shifts ≥ 5 minutes — the same
// threshold as iOS ETAManager.shouldUpdateETA — and recomputes at most once
// per minute per order or after the driver moves ~250m.

const RECOMPUTE_MS = 60_000;
const RECOMPUTE_METERS = 250;
const WRITE_THRESHOLD_SEC = 5 * 60; // iOS shouldUpdateETA(5.0)
const PICKUP_SERVICE_SEC = 300; // iOS adds 5 min at the restaurant
const FALLBACK_SPEED_MPS = 8.9; // ~20 mph straight-line fallback
const OSRM = 'https://router.project-osrm.org/route/v1';

// iOS ETAManager.transportType parity — walking couriers get pedestrian
// ETAs; the OSRM demo server exposes foot/bike/car profiles.
const OSRM_PROFILE = { walking: 'foot', bicycle: 'bike' };
const FALLBACK_SPEED = { walking: 1.4, bicycle: 4.5 }; // ~3mph walk, ~10mph bike

function osrmProfile(vehicleType) {
  return OSRM_PROFILE[(vehicleType || '').toLowerCase()] || 'driving';
}
function fallbackSpeed(vehicleType) {
  return FALLBACK_SPEED[(vehicleType || '').toLowerCase()] || FALLBACK_SPEED_MPS;
}

const lastCheck = new Map(); // orderId -> { at, from: {lat,lng} }

const PRE_PICKUP = new Set([
  DriverOrderStatus.ACCEPTED,
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
]);

function meters(a, b) {
  const ft = distanceFeet(a, b);
  return ft == null ? null : ft * 0.3048;
}

function destinationFor(order) {
  // Pre-pickup the driver is headed to the restaurant; after pickup, to the
  // customer (iOS switches ETA target on the same boundary).
  const restaurant = toLatLng(
    order.pickupLocation || order.restaurantLocation?.coordinates || order.restaurantLocation
  );
  const customer = toLatLng(
    order.deliveryLocation ||
    order.deliveryAddress?.coordinates ||
    order.customerLocation?.coordinates
  );
  if (PRE_PICKUP.has(order.status)) {
    return restaurant
      ? { to: restaurant, field: 'estimatedPickupTime', serviceSec: PICKUP_SERVICE_SEC }
      : null;
  }
  return customer
    ? { to: customer, field: 'estimatedDeliveryTime', serviceSec: 0 }
    : null;
}

async function routeSeconds(from, to, vehicleType) {
  const profile = osrmProfile(vehicleType);
  try {
    const res = await fetch(
      `${OSRM}/${profile}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=false`,
      { signal: AbortSignal.timeout(8000) }
    );
    const json = await res.json();
    const dur = json?.routes?.[0]?.duration;
    if (Number.isFinite(dur) && dur > 0) return dur;
  } catch {
    /* fall through to the straight-line estimate */
  }
  const m = meters(from, to);
  return m == null ? null : m / fallbackSpeed(vehicleType);
}

// Full road geometry for map polylines — the same OSRM driving profile the
// ETA calc uses (Google-style routed line instead of a straight segment).
// Returns [{lat,lng}, ...] or null when routing fails; callers fall back to
// a straight line.
export async function routeGeometry(from, to) {
  try {
    const res = await fetch(
      `${OSRM}/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`,
      { signal: AbortSignal.timeout(8000) }
    );
    const json = await res.json();
    const coords = json?.routes?.[0]?.geometry?.coordinates;
    if (Array.isArray(coords) && coords.length > 1) {
      return coords.map(([lng, lat]) => ({ lat, lng }));
    }
  } catch {
    /* fall through — caller uses the straight segment */
  }
  return null;
}

// Throttled ETA refresh. `order` is a parsed accepted-order; `driverCoords`
// is {lat,lng}; `driverId` lets us also update the acceptedOrders mirror doc.
export async function maybeUpdateOrderETA(order, driverCoords, driverId, vehicleType) {
  if (!order?.orderId || !driverCoords) return;
  const oid = order.orderId || order.id;
  const dest = destinationFor(order);
  if (!dest) return;

  const last = lastCheck.get(oid);
  const now = Date.now();
  if (last && now - last.at < RECOMPUTE_MS) {
    const moved = meters(last.from, driverCoords);
    if (moved != null && moved < RECOMPUTE_METERS) return;
  }
  lastCheck.set(oid, { at: now, from: driverCoords });

  const secs = await routeSeconds(driverCoords, dest.to, vehicleType);
  if (secs == null) return;
  const eta = new Date(now + (secs + dest.serviceSec) * 1000);

  // iOS shouldUpdateETA: write only when the stored ETA is missing or moved
  // by ≥ 5 minutes — avoids a write storm on every location tick.
  const cur = order[dest.field];
  const current = cur instanceof Date ? cur.getTime() : cur?.toMillis?.() ?? null;
  if (current && Math.abs(current - eta.getTime()) < WRITE_THRESHOLD_SEC * 1000) return;

  const payload = { [dest.field]: Timestamp.fromDate(eta), updatedAt: Timestamp.now() };
  await updateDoc(doc(db, 'orders', oid), payload).catch(e =>
    console.warn('order ETA update failed:', e)
  );
  if (driverId) {
    await updateDoc(doc(db, 'drivers', driverId, 'acceptedOrders', oid), payload).catch(() => {});
  }
}
